import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faSpinner,
  faCheck,
  faMoneyBillWave,
  faBoxes,
  faPercent,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";

const fetchCsrfToken = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/session/token`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  if (!response.ok) throw new Error("Failed to fetch CSRF token");
  return await response.text();
};

const formatCurrency = (amount) => `GH₵ ${parseFloat(amount || 0).toFixed(2)}`;

export default function UnifiedRefundModal({
  order,
  onClose,
  onRefundSuccess,
}) {
  const [refundType, setRefundType] = useState("items");
  const [refundableItems, setRefundableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [reason, setReason] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOrderRefundData();
  }, [order]);

  const fetchOrderRefundData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/refund-data`,
        { credentials: "include" },
      );
      const data = await response.json();
      if (data.success) {
        setOrderData(data.order);
        setRefundableItems(data.items);

        // Initialize selected items
        const initialSelected = {};
        data.items.forEach((item) => {
          initialSelected[item.id] = 0;
        });
        setSelectedItems(initialSelected);
      }
    } catch (error) {
      console.error("Error fetching refund data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (itemId, maxQuantity, newQuantity) => {
    const quantity = Math.min(
      maxQuantity,
      Math.max(0, parseInt(newQuantity) || 0),
    );
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: quantity,
    }));
  };

  const calculateItemRefundTotal = () => {
    let total = 0;
    refundableItems.forEach((item) => {
      const quantity = selectedItems[item.id] || 0;
      if (quantity > 0) {
        total += item.price * quantity;
      }
    });
    return total;
  };

  const getRefundDetails = () => {
    if (refundType === "full") {
      const remainingRefundable = orderData?.remaining_balance || 0;
      return {
        total: remainingRefundable,
        description: `Full refund of remaining balance (${formatCurrency(remainingRefundable)})`,
      };
    } else {
      const items = [];
      refundableItems.forEach((item) => {
        const quantity = selectedItems[item.id] || 0;
        if (quantity > 0) {
          items.push({
            id: item.id,
            name: item.name,
            quantity: quantity,
            price: item.price,
            amount: item.price * quantity,
          });
        }
      });
      return {
        total: calculateItemRefundTotal(),
        items: items,
        description: `${items.length} item(s) refunded`,
      };
    }
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      alert("Please provide a reason for the refund");
      return;
    }

    const refundDetails = getRefundDetails();

    if (refundType === "items" && refundDetails.items?.length === 0) {
      alert("Please select items to refund or choose full order refund");
      return;
    }

    if (refundDetails.total <= 0) {
      alert("No refundable amount available");
      return;
    }

    setProcessing(true);
    try {
      const csrfToken = await fetchCsrfToken();

      let payload;
      if (refundType === "full") {
        payload = {
          refund_type: "full",
          reason: reason,
        };
      } else {
        payload = {
          refund_type: "items",
          items: refundDetails.items,
          reason: reason,
        };
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/refund-unified`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();
      if (result.success) {
        onRefundSuccess(result);
        onClose();
      } else {
        alert(result.error || "Failed to process refund");
      }
    } catch (error) {
      console.error("Error processing refund:", error);
      alert("An error occurred while processing the refund");
    } finally {
      setProcessing(false);
    }
  };

  const refundDetails = getRefundDetails();
  const canRefundFull = orderData?.remaining_balance > 0;

  // ✅ Calculate new order total after this refund
  const calculateNewOrderTotal = () => {
    const currentTotal = orderData?.order_total || 0;
    const refundAmount = refundDetails.total || 0;
    // The order total already includes previous refunds, so just subtract this refund
    return Math.max(0, currentTotal - refundAmount);
  };

  if (loading) {
    return (
      <div className="modal">
        <div className="overlay" onClick={onClose}></div>
        <div className="modal-content" style={{ maxWidth: "600px" }}>
          <div
            className="modal-body"
            style={{ textAlign: "center", padding: "40px" }}
          >
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#FF6B35" />
            <p>Loading refund data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="overlay" onClick={onClose}></div>
      <div className="modal-content" style={{ maxWidth: "650px" }}>
        <div className="modal-body">
          <div className="modal-header">
            <p>
              <FontAwesomeIcon
                icon={faMoneyBillWave}
                style={{ marginRight: "10px", color: "#EF4444" }}
              />
              Process Refund - Order #{order.id}
            </p>
            <button className="close-modal" onClick={onClose}>
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>

          <div style={{ padding: "16px" }}>
            {/* Order Summary */}
            <div
              style={{
                marginBottom: "20px",
                padding: "12px",
                backgroundColor: "#F9FAFB",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                <div>
                  <p>
                    <strong>Customer:</strong>{" "}
                    {orderData?.guest_name || "Guest"}
                  </p>
                  <p>
                    <strong>Original Total:</strong>{" "}
                    {formatCurrency(
                      orderData?.original_total || orderData?.order_total || 0,
                    )}
                  </p>
                  <p>
                    <strong>Current Total:</strong>{" "}
                    <span style={{ color: "#4CAF50" }}>
                      {formatCurrency(orderData?.order_total || 0)}
                    </span>
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Already Refunded:</strong>{" "}
                    <span style={{ color: "#DC2626" }}>
                      {formatCurrency(orderData?.total_refunded || 0)}
                    </span>
                  </p>
                  <p>
                    <strong>Remaining Balance:</strong>{" "}
                    <span style={{ color: "#4CAF50" }}>
                      {formatCurrency(orderData?.remaining_balance || 0)}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Refund Type Selection */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "12px",
                  fontWeight: "600",
                }}
              >
                <FontAwesomeIcon
                  icon={faReceipt}
                  style={{ marginRight: "8px", color: "#FF6B35" }}
                />
                Refund Type
              </label>
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setRefundType("items")}
                  style={{
                    flex: 1,
                    padding: "12px",
                    backgroundColor:
                      refundType === "items" ? "#FF6B35" : "#F3F4F6",
                    color: refundType === "items" ? "white" : "#374151",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faBoxes}
                    style={{ marginRight: "8px" }}
                  />
                  Refund Specific Items
                </button>
                <button
                  onClick={() => setRefundType("full")}
                  disabled={!canRefundFull}
                  style={{
                    flex: 1,
                    padding: "12px",
                    backgroundColor:
                      refundType === "full" ? "#DC2626" : "#F3F4F6",
                    color:
                      refundType === "full"
                        ? "white"
                        : canRefundFull
                          ? "#374151"
                          : "#9CA3AF",
                    border: "none",
                    borderRadius: "8px",
                    cursor: canRefundFull ? "pointer" : "not-allowed",
                    fontWeight: "500",
                    opacity: canRefundFull ? 1 : 0.6,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faMoneyBillWave}
                    style={{ marginRight: "8px" }}
                  />
                  Full Order Refund
                </button>
              </div>
            </div>

            {/* Items Selection (only for item refund) */}
            {refundType === "items" && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "12px" }}>Select Items to Refund</h4>
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#F9FAFB",
                      }}
                    >
                      <tr style={{ borderBottom: "1px solid #E5E7EB" }}>
                        <th style={{ textAlign: "left", padding: "8px" }}>
                          Item
                        </th>
                        <th style={{ textAlign: "center", padding: "8px" }}>
                          Price
                        </th>
                        <th style={{ textAlign: "center", padding: "8px" }}>
                          Available
                        </th>
                        <th style={{ textAlign: "center", padding: "8px" }}>
                          Quantity
                        </th>
                        <th style={{ textAlign: "right", padding: "8px" }}>
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {refundableItems.map((item) => {
                        if (item.remaining_quantity === 0) return null;
                        const refundQty = selectedItems[item.id] || 0;
                        const refundAmount = item.price * refundQty;
                        return (
                          <tr
                            key={item.id}
                            style={{ borderBottom: "1px solid #F3F4F6" }}
                          >
                            <td style={{ padding: "8px" }}>{item.name}</td>
                            <td style={{ textAlign: "center", padding: "8px" }}>
                              {formatCurrency(item.price)}
                            </td>
                            <td
                              style={{
                                textAlign: "center",
                                padding: "8px",
                                color: "#6B7280",
                              }}
                            >
                              {item.remaining_quantity}
                            </td>
                            <td style={{ textAlign: "center", padding: "8px" }}>
                              <input
                                type="number"
                                min="0"
                                max={item.remaining_quantity}
                                value={refundQty}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    item.id,
                                    item.remaining_quantity,
                                    e.target.value,
                                  )
                                }
                                style={{
                                  width: "70px",
                                  padding: "4px",
                                  textAlign: "center",
                                  border: "1px solid #E5E7EB",
                                  borderRadius: "4px",
                                }}
                              />
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                padding: "8px",
                                color: "#DC2626",
                              }}
                            >
                              {refundAmount > 0
                                ? formatCurrency(refundAmount)
                                : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Refund Summary */}
            {refundDetails.total > 0 && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "12px",
                  backgroundColor: "#FEE2E2",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <strong>Refund Summary:</strong>
                  <strong style={{ color: "#DC2626" }}>
                    {formatCurrency(refundDetails.total)}
                  </strong>
                </div>
                {refundType === "items" && refundDetails.items && (
                  <div style={{ fontSize: "12px", color: "#6B7280" }}>
                    {refundDetails.items.map((item, idx) => (
                      <div key={idx}>
                        {item.quantity}x {item.name} ={" "}
                        {formatCurrency(item.amount)}
                      </div>
                    ))}
                  </div>
                )}
                {/* ✅ FIXED: New order total after this refund */}
                <div
                  style={{
                    marginTop: "8px",
                    paddingTop: "8px",
                    borderTop: "1px solid #FCA5A5",
                    fontSize: "13px",
                    fontWeight: "500",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Current Order Total:</span>
                    <span>{formatCurrency(orderData?.order_total || 0)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#DC2626",
                    }}
                  >
                    <span>Refund Amount:</span>
                    <span>-{formatCurrency(refundDetails.total)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "4px",
                      paddingTop: "4px",
                      borderTop: "1px solid #FCA5A5",
                      fontWeight: "bold",
                      color: "#4CAF50",
                    }}
                  >
                    <span>New Order Total:</span>
                    <span>{formatCurrency(calculateNewOrderTotal())}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Reason for Refund *
              </label>
              <textarea
                placeholder="Enter reason for refund (e.g., customer complaint, wrong order, quality issue, etc.)"
                rows="3"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            {/* Actions */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#F3F4F6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={processing || refundDetails.total === 0}
                style={{
                  padding: "10px 20px",
                  backgroundColor:
                    refundDetails.total > 0 ? "#DC2626" : "#9CA3AF",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: refundDetails.total > 0 ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {processing ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faCheck} />
                )}
                Process Refund ({formatCurrency(refundDetails.total)})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
